import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import BuilderContext from 'common/Context';
import FroalaEditor from '../../../../../FroalaEditor';
import DraggableContainer from '../DraggableContainer';
import DeleteButton from '../DeleteButton';
import ResizableContainer from '../ResizableContainer';
import useVocabularyStore from '../../useVocabularyStore';
import styles from './TextArea.module.scss';
import froalaConfig from './FroalaConfig';

const TextArea = ({ boundsRef, height, id, text, left, minDimensions, top, type, width, vocabId }) => {
  const vocabularyTerms = useVocabularyStore(state => state.vocabularyTerms);
  const [editMode, setEditMode] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const { textAreaDispatch, questionId, loading, vocabLinkOption } = useContext(BuilderContext);
  const label = `Text area ${id.toUpperCase()}`;
  const MOVEMENT_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

  const vocabTerm = (vocabId && !loading) ?
    vocabularyTerms.find(vocab => vocab.value === vocabId) || { label: '' } :
    { label: '' };

  const handleKeyDown = (e) => {
    if (MOVEMENT_KEYS.includes(e.key)) {
      e.preventDefault();
      textAreaDispatch({ payload: { height, id, key: e.key, left, text, top, width }, type: 'MOVE_WITH_ARROW_KEY' });
    }
  };

  const updateText = (value) => {
    textAreaDispatch({ payload: { height, id, left, text: value, top, width }, type: 'UPDATE' });
  };

  const renderTextAreaContent = () => {
    if (editMode) {
      return (
        <FroalaEditor
          customConfig={froalaConfig(questionId)}
          model={text}
          tag="div"
          text={text}
          onModelChange={newContent => updateText(newContent)}
        />
      );
    }

    if (vocabLinkOption === 'images') {
      return (
        <div className={styles.imageCard}>
          <div className={styles.imageContainer}>
            <img alt={vocabTerm.term} src={vocabTerm.image} />
          </div>
        </div>
      );
    }

    return (
      <div
        className={styles.textarea}
        dangerouslySetInnerHTML={{ __html: vocabTerm.definition || text }}
      />
    );
  };

  return (
    <DraggableContainer
      draggable={!editMode && !isResizing}
      handleKeyDown={handleKeyDown}
      item={{ height, id, label, left, text, title: label, top, type, width, vocabId }}
      minDimensions={minDimensions}
      type="TextArea"
    >
      <ResizableContainer
        highlighted={false}
        parentEl={boundsRef && boundsRef.current && boundsRef.current.firstChild}
        onResizeStart={() => setIsResizing(true)}
        onResizeStop={(e, newDimensions) => {
          setIsResizing(false);
          textAreaDispatch({ payload: { id, left, text, top, ...newDimensions }, type: 'UPDATE' });
        }}
      >
        <div className={styles.textareaContainer}>
          {renderTextAreaContent()}
          <div
            aria-label="edit toggle"
            className={`fa ${editMode ? 'fa-edit' : 'fa-arrows'} ${styles.editIcon}`}
            role="button"
            tabIndex={0}
            onClick={() => setEditMode(!editMode)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setEditMode(!editMode);
            }}
          />
          <DeleteButton
            id={id}
            label={`Delete ${label}`}
            type={type}
          />
        </div>
      </ResizableContainer>
    </DraggableContainer>
  );
};

TextArea.propTypes = {
  boundsRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) })
  ]),
  height: PropTypes.number,
  id: PropTypes.string.isRequired,
  left: PropTypes.number.isRequired,
  minDimensions: PropTypes.shape({ height: PropTypes.number, width: PropTypes.number }).isRequired,
  text: PropTypes.string.isRequired,
  top: PropTypes.number.isRequired,
  type: PropTypes.string.isRequired,
  vocabId: PropTypes.number,
  width: PropTypes.number,
};

TextArea.defaultProps = {
  height: 80,
  vocabId: null,
  width: 224,
};

export default TextArea;
